<template>
    <div>
        <div id="Dashboard" class="container is-fluid">
            <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet" />

            <!-- Header -->
            <HeroContainer title="Margin Chart Dashboard" />

            <section id="filters" style="display: flex; justify-content: center">
                <div style="width: 500px">
                    <div class="field is-horizontal">
                        <div class="field-label is-normal">
                            <label class="label">Customer</label>
                        </div>
                        <div class="field-body">
                            <div class="field">
                                <span class="select">
                                    <select id="customer_select" v-model="filter_customer">
                                        <option value="">All Customers</option>
                                        <option
                                            v-for="item in customer_items"
                                            :key="'cust_' + item"
                                            :value="item"
                                        >
                                            {{ item }}
                                        </option>
                                    </select>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="field is-horizontal">
                        <div class="field-label is-normal">
                            <label class="label">Group by</label>
                        </div>
                        <div class="field-body">
                            <div class="field">
                                <span class="select">
                                    <select id="period" v-model="period">
                                        <option disabled hidden value="" style="color: grey">
                                            Period
                                        </option>
                                        <option value="day">Day</option>
                                        <option value="week">Week</option>
                                        <option value="month">Month</option>
                                        <option value="quarter">Quarter</option>
                                    </select>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="field is-horizontal">
                        <div class="field-label is-normal">
                            <label class="label">Period</label>
                        </div>
                        <div class="field-body">
                            <div class="field has-addons">
                                <p class="control">
                                    <span class="select">
                                        <select
                                            id="date_range"
                                            v-model="date_range"
                                            @change="change_date(date_range)"
                                        >
                                            <option disabled hidden value="" style="color: grey">
                                                Date Presets
                                            </option>
                                            <option value="today">Today</option>
                                            <option value="week">Week</option>
                                            <option value="this_month">This Month</option>
                                            <option value="prev_month">Prev Month</option>
                                            <option value="this_quarter">This Quarter</option>
                                            <option value="prev_quarter">Prev Quarter</option>
                                            <option value="this_year">This Year</option>
                                            <option value="prev_year">Prev Year</option>
                                            <option value="all">All</option>
                                            <option disabled hidden value="custom">Custom</option>
                                        </select>
                                    </span>
                                </p>
                                <p class="control is-expanded">
                                    <VueDatePicker
                                        v-model="range"
                                        :enable-time-picker="false"
                                        range
                                    />
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="buttons is-right">
                        <button @click="reset" class="button is-small is-white">Reset</button>
                        <button @click="ajax_get_dashboard" class="button is-info">Filter</button>
                    </div>
                </div>
            </section>

            <section id="plot_data">
                <PlotlyFigure
                    :plot_data="plotData"
                    :layout="layout"
                    :options="options"
                    v-on:plot_click="plot_click"
                    :key="figureKey"
                >
                </PlotlyFigure>
            </section>

            <section id="dashboard_data" v-if="queryData.data.length > 0">
                <DataTable2
                    :raw_data="queryData"
                    :name="'dash_sales_detail'"
                    :columns_from_parent="[
                        {
                            label: 'Part',
                            field: 'part_number',
                            type: 'text',
                        },
                        {
                            label: 'Description',
                            field: 'description',
                            type: 'text',
                        },
                        {
                            label: 'Invoice',
                            field: 'invoice',
                            type: 'text',
                        },
                        {
                            label: 'Margin',
                            field: 'margin',
                            type: 'number',
                            formatFn: function (value) {
                                return numeral(value).format('0,0,0,0,0.00%')
                            },
                        },
                        {
                            label: 'Company PO',
                            field: 'company_po_number',
                            type: 'text',
                        },
                        {
                            label: 'Profit',
                            field: 'profit',
                            type: 'number',
                            formatFn: function (value) {
                                return numeral(value).format('$0,0,0,0,0.00')
                            },
                        },
                        {
                            label: 'Unit Cost',
                            field: 'unit_cost',
                            type: 'number',
                            formatFn: function (value) {
                                return numeral(value).format('$0,0,0,0,0.00')
                            },
                        },
                        {
                            label: 'Unit Price',
                            field: 'unit_price',
                            type: 'number',
                            formatFn: function (value) {
                                return numeral(value).format('$0,0,0,0,0.00')
                            },
                        },
                        {
                            label: 'Total Cost',
                            field: 'total_cost',
                            type: 'number',
                            formatFn: function (value) {
                                return numeral(value).format('$0,0,0,0,0.00')
                            },
                        },
                        {
                            label: 'Total Price',
                            field: 'total_price',
                            type: 'number',
                            formatFn: function (value) {
                                return numeral(value).format('$0,0,0,0,0.00')
                            },
                        },
                        {
                            label: 'Bill Name',
                            field: 'bill_name',
                            type: 'text',
                        },
                        {
                            label: 'Vendor',
                            field: 'vendor_name',
                            type: 'text',
                        },
                        {
                            label: 'Dept Name',
                            field: 'dept_name',
                            type: 'text',
                        },
                        {
                            label: 'Orig PO',
                            field: 'original_po_number',
                            type: 'text',
                        },
                        {
                            label: 'Qty Ship',
                            field: 'qty_ship',
                            type: 'number',
                            formatFn: function (value) {
                                return numeral(value).format('0,0,0,0,0')
                            },
                        },
                        {
                            label: 'Reference',
                            field: 'reference',
                            type: 'text',
                        },
                        {
                            label: 'Invoice Date',
                            field: 'invoice_date',
                            type: 'text',
                            formatFn: function (value) {
                                return value.split('T')[0]
                            },
                        },
                        {
                            label: 'Post Date',
                            field: 'post_date',
                            type: 'text',
                            formatFn: function (value) {
                                return value.split('T')[0]
                            },
                        },
                        {
                            label: 'Time Created',
                            field: 'time_created',
                            type: 'text',
                            formatFn: function (value) {
                                return value.split('T')[0]
                            },
                        },
                    ]"
                />
                <div class="buttons is-right">
                    <button
                        class="button is-info"
                        style="margin: 0.5em 0 0 0"
                        @click="
                            $emit('download-csv', [queryData.data, 'MarginChartDashboard', null])
                        "
                    >
                        Download CSV
                    </button>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
    import DataTable2 from '@/components/shared/DataTable2.vue'
    import PlotlyFigure from '@/components/shared/Plotly.vue'
    import moment from 'moment'
    import VueDatePicker from '@vuepic/vue-datepicker'
    import { getDates } from '@/helpers/dates.js'
    import { DASHBOARD_MARGIN_CHART, DASHBOARD_MARGIN_DETAIL } from '@/data/url'
    import config from '@/services/config'
    import HeroContainer from '@/components/shared/HeroContainer.vue'
    import { useDropdownsStore } from '@/stores/dropdownsStore'

    export default {
        name: 'DashboardMarginChart',
        components: {
            DataTable2,
            PlotlyFigure,
            VueDatePicker,
            HeroContainer,
        },
        computed: {
            dropdownsStore() {
                return useDropdownsStore()
            },
            customer_items() {
                return this.dropdownsStore.customer
            },
            queryData() {
                let raw_query = [...this.queryDataRaw]
                if (Object.keys(raw_query).length == 0) {
                    return { site: '', data: [] }
                }
                return { site: '', data: raw_query }
            },
            plotData() {
                let data = [
                    { type: 'scatter', name: 'margin', x: [], y: [], line: { shape: 'spline' } },
                ]
                if (Object.prototype.hasOwnProperty.call(this.plotDataRaw, 'margin')) {
                    let sales_data = this.plotDataRaw.margin
                    for (let margin of sales_data) {
                        data[0].x.push(margin.period)
                        data[0].y.push(this.numeral(margin.margin).format('0,0,0,0,0,0.0%'))
                    }
                }
                return data
            },
        },
        data: function () {
            return {
                numeral: require('numeral'),
                figureKey: 0,
                filter_customer: '',
                period: 'month',
                period_last_filtered: 'month',
                date_range: 'this_year',
                range: [moment().startOf('year').toDate(), moment().endOf('year').toDate()],
                queryDataRaw: [],
                plotDataRaw: [],
                displayQuoteButton: false,
                is_bpa_noquote: false,
                layout: {
                    legend: {
                        x: 0,
                        y: 1.3,
                    },
                    yaxis: {
                        title: {
                            text: '% Margin',
                            font: {
                                size: 18,
                                color: '#1f77b4',
                            },
                        },
                        rangemode: 'tozero',
                    },
                },
                options: {
                    displayModeBar: false,
                    responsive: true,
                },
            }
        },
        mounted: function () {
            this.ajax_get_dashboard()
        },
        watch: {
            plotData: function () {
                this.figureKey += 1
            },
        },
        methods: {
            getDates,

            authHeaders() {
                const headers = { 'Content-Type': 'application/json' }
                const sessionToken = config.sessionToken
                if (sessionToken) headers.Authorization = `Bearer ${sessionToken}`
                return headers
            },
            plot_click(click_data) {
                let date = click_data.points[0].x
                let period = this.period_last_filtered

                let dt = moment.utc(date)
                let dt2 = moment.utc(dt).endOf(period)
                this.ajax_get_detail(dt.toISOString(), dt2.toISOString())
            },
            reset() {
                this.period = 'month'
                ;((this.period_last_filtered = 'month'), (this.date_range = 'this_year'))
                this.range = [moment().startOf('year').toDate(), moment().endOf('year').toDate()]
                this.queryDataRaw = []
                this.plotDataRaw = []
            },
            change_date(date_range) {
                let range = this.getDates(date_range)
                if (range) {
                    this.range = range
                }
            },
            set_searching(is_searching) {
                this.searchStore.set_ajax_searching(is_searching)
            },
            ajax_get_dashboard() {
                this.plotDataRaw = []
                this.queryDataRaw = []
                this.period_last_filtered = this.period
                const url = `${config.backendURL || ''}${DASHBOARD_MARGIN_CHART}`
                let begin_date = moment.utc(this.range[0]).format('YYYY-MM-DD')
                let end_date = moment.utc(this.range[1]).endOf('day').format('YYYY-MM-DD')

                this.set_searching(true)
                let this_vue = this
                function set_dash(this_vue, data) {
                    this_vue.plotDataRaw = data
                }
                fetch(url, {
                    method: 'POST',
                    headers: this.authHeaders(),
                    body: JSON.stringify({
                        begin_date: begin_date,
                        end_date: end_date,
                        period: this.period,
                        customer: this.filter_customer,
                    }),
                })
                    .then((res) => res.json())
                    .then((res) => {
                        this_vue.set_searching(false)
                        if (res.success) {
                            set_dash(this_vue, res.data)
                        }
                    })
                    .catch((e) => {
                        this_vue.set_searching(false)
                        console.error(JSON.stringify(e.message))
                    })
            },
            ajax_get_detail(begin_date, end_date) {
                this.queryDataRaw = []
                const url = `${config.backendURL || ''}${DASHBOARD_MARGIN_DETAIL}`
                this.set_searching(true)
                let this_vue = this
                function set_detail(this_vue, data) {
                    this_vue.queryDataRaw = data.sales
                }
                fetch(url, {
                    method: 'POST',
                    headers: this.authHeaders(),
                    body: JSON.stringify({
                        begin_date: begin_date,
                        end_date: end_date,
                        customer: this.filter_customer,
                    }),
                })
                    .then((res) => res.json())
                    .then((res) => {
                        this_vue.set_searching(false)
                        if (res.success) {
                            set_detail(this_vue, res.data)
                        }
                    })
                    .catch((e) => {
                        this_vue.set_searching(false)
                        console.error(JSON.stringify(e.message))
                    })
            },
        },
    }
</script>
